import type { ChangeEvent } from 'react';
import { cn } from '@/lib/utils';
import { STRINGS } from '@/strings/strings';

const DEFAULT_MAX_LENGTH = 300;
// Counter shifts to the warm-accent token when the user crosses 90% of the
// cap — earlier (e.g. 80%) would warn mid-flow on perfectly natural prompts.
const WARN_THRESHOLD = 0.9;

interface ThemeInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  id?: string;
  /**
   * Hard ceiling for input length (chars). Both <input maxLength={...}> and
   * a defensive paste-clamp inside onChange enforce it; backend mirrors
   * via @MaxLength(300) on the DTO.
   */
  maxLength?: number;
  /**
   * Override the NORMAL-state counter colour (default `text-dream-navy/60`).
   * Used by the series theme field for legibility on AA's light-blue bg
   * (navy) and KP's dark bg (white). The near-cap (gold) + at-cap (error)
   * warning colours still win. Single-story passes nothing → unchanged.
   */
  counterClassName?: string;
}

export function ThemeInput({
  value,
  onChange,
  placeholder,
  disabled,
  id,
  maxLength = DEFAULT_MAX_LENGTH,
  counterClassName,
}: ThemeInputProps) {
  const length = value.length;
  const overCap = length > maxLength;
  const nearCap = !overCap && length >= Math.floor(maxLength * WARN_THRESHOLD);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    // <input maxLength> blocks typing past the ceiling, but `value`
    // setters from paste / programmatic input can still exceed it.
    onChange(next.length > maxLength ? next.slice(0, maxLength) : next);
  };

  const counterText = STRINGS.builder.shared.charCounter
    .replace('{current}', String(length))
    .replace('{max}', String(maxLength));

  return (
    <div className="space-y-1">
      <input
        id={id}
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className={cn(
          'builder-field h-11 w-full px-3 text-base text-dream-navy outline-none placeholder:text-dream-navy/50 disabled:bg-white/70',
          disabled && 'italic opacity-60',
        )}
      />
      <div
        className={cn(
          'text-right text-xs tabular-nums text-dream-navy/60',
          counterClassName,
          nearCap && 'text-dream-gold',
          overCap && 'text-[color:var(--color-error)]',
        )}
        aria-live="polite"
      >
        {counterText}
      </div>
    </div>
  );
}
